/**
 * CommandPalette — global ⌘K / Ctrl+K palette for instant navigation + actions.
 *
 * Triggers on:
 *   - cmd/ctrl + K
 *   - cmd/ctrl + /
 *
 * Provides fuzzy-searchable access to: routes, models (lazy-loaded), and quick actions.
 * Designed to feel like Linear / Figma / Notion. Premium signal.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Sparkles, Image as ImageIcon, Video, Wand2, Home, BarChart3, FolderOpen, Users, CreditCard, BookOpen, Layers, Compass, Zap, Brush, Box } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUTES = [
  { id: 'home',      label: 'Home',                hint: 'Landing page',       to: '/',           icon: Home },
  { id: 'generate',  label: 'Generate (Studio)',   hint: 'Image + video',      to: '/generate',   icon: Sparkles, kbd: 'G' },
  { id: 'i2v',       label: 'Image → Video',       hint: 'Animate stills',     to: '/image-to-video', icon: Video, kbd: 'V' },
  { id: 'canvas',    label: 'Sketch Canvas',       hint: 'Draw → inpaint',     to: '/canvas',     icon: Brush, kbd: 'C' },
  { id: 'models',    label: 'Browse 150 Models',   hint: 'Full catalog',       to: '/models',     icon: Layers, kbd: 'M' },
  { id: 'community', label: 'Community Feed',      hint: 'See what others made', to: '/community', icon: Users },
  { id: 'library',   label: 'My Library',          hint: 'Your generations',   to: '/library',    icon: FolderOpen, kbd: 'L' },
  { id: 'dashboard', label: 'Dashboard',           hint: 'Stats + credits',    to: '/dashboard',  icon: BarChart3 },
  { id: 'pricing',   label: 'Pricing',             hint: 'Buy credits',        to: '/pricing',    icon: CreditCard },
  { id: 'blog',      label: 'Blog',                hint: 'Latest articles',    to: '/blog',       icon: BookOpen },
];

const QUICK_ACTIONS = [
  { id: 'open-studio',  label: 'New image generation',  hint: 'Open studio',          to: '/generate', icon: ImageIcon },
  { id: 'open-video',   label: 'New video generation',  hint: 'Sora 2',               to: '/generate?kind=video', icon: Video },
  { id: 'open-i2v',     label: 'Animate an image',      hint: 'Image → video',        to: '/image-to-video', icon: Wand2 },
  { id: 'open-3d',      label: '3D models',             hint: 'Stable 3D, Shap-E',    to: '/models?cat=3d', icon: Box },
  { id: 'discover',     label: 'Discover trending',     hint: 'Community feed',       to: '/community', icon: Compass },
];

function score(query, text, isLabel = false) {
  if (!query) return 0;
  const q = query.toLowerCase();
  const t = (text || '').toLowerCase();
  if (t === q) return 1000;
  if (t.startsWith(q)) return 500;
  if (t.includes(q)) return 200;
  if (!isLabel) return -1;  // Only fuzzy-match on labels, not hints
  // Acronym match: e.g. "ig" matches "Image Generation"
  const acronym = t.split(/\s+/).map((w) => w[0]).join('');
  if (acronym.includes(q)) return 100;
  // Fuzzy — chars in order, but ONLY for queries ≥3 chars
  if (q.length < 3) return -1;
  let i = 0, j = 0;
  while (i < q.length && j < t.length) {
    if (q[i] === t[j]) i++;
    j++;
  }
  return i === q.length ? 50 : -1;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [models, setModels] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Lazy-load models the first time the palette opens
  useEffect(() => {
    if (!open || models.length > 0) return;
    axios.get(`${API}/models`).then((r) => setModels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [open, models.length]);

  // Global hotkey + custom open event
  useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === '/');
      if (isCmdK) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('d2-cmdk-open', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('d2-cmdk-open', onOpenEvent);
    };
  }, [open]);

  // Focus input + reset state when opening
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      setQuery('');
      setActive(0);
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  const results = useMemo(() => {
    const candidates = [
      ...ROUTES.map((r) => ({ ...r, group: 'GO TO' })),
      ...QUICK_ACTIONS.map((a) => ({ ...a, group: 'QUICK ACTIONS' })),
      ...models.map((m) => ({
        id: `model-${m.id}`,
        label: m.name,
        hint: `${m.kind?.toUpperCase()} · ${m.tier?.toUpperCase()} · ${m.desc || ''}`.slice(0, 70),
        to: `/generate?model=${encodeURIComponent(m.id)}`,
        icon: m.kind === 'video' ? Video : ImageIcon,
        group: 'MODELS',
      })),
    ];
    if (!query) {
      return candidates.slice(0, 12);
    }
    const scored = candidates
      .map((c) => ({ c, s: Math.max(score(query, c.label, true), score(query, c.hint, false)) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 20)
      .map((x) => x.c);
    return scored;
  }, [query, models]);

  const select = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    if (item.to) navigate(item.to);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[active]);
    }
  };

  if (!open) return null;

  // Group results
  const groups = results.reduce((acc, r) => {
    const g = r.group || 'RESULTS';
    if (!acc[g]) acc[g] = [];
    acc[g].push(r);
    return acc;
  }, {});

  let globalIdx = -1;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-4 bg-black/60 backdrop-blur-md d2-modal-backdrop"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl border border-white/[0.08] bg-[#0F0F15]/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(255,107,74,0.25)] overflow-hidden d2-modal-card"
      >
        {/* Search */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            placeholder="Search models, pages, actions…"
            className="flex-1 bg-transparent outline-none text-white placeholder-white/30 text-sm"
            data-testid="cmdk-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded bg-white/[0.05] border border-white/[0.08] text-[10px] font-mono text-white/40 uppercase">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {results.length === 0 && (
            <div className="px-5 py-12 text-center text-white/40 text-sm">
              No matches. Try <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 text-xs">flux</kbd>, <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 text-xs">sora</kbd>, or <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 text-xs">pricing</kbd>.
            </div>
          )}
          {Object.entries(groups).map(([groupName, items]) => (
            <div key={groupName} className="mb-2">
              <div className="px-5 py-1.5 text-[9px] font-bold tracking-[0.25em] text-white/30 uppercase">
                {groupName}
              </div>
              {items.map((item) => {
                globalIdx += 1;
                const isActive = globalIdx === active;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => select(item)}
                    onMouseEnter={() => setActive(globalIdx)}
                    data-testid={`cmdk-result-${item.id}`}
                    className={`w-full px-5 py-2.5 flex items-center gap-3 text-left transition-colors ${
                      isActive ? 'bg-[#FF6B4A]/12' : 'hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                      isActive ? 'bg-[#FF6B4A]/20 text-[#FF6B4A]' : 'bg-white/[0.05] text-white/50'
                    }`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-white/85'}`}>
                        {item.label}
                      </div>
                      {item.hint && (
                        <div className="text-[11px] text-white/40 truncate">{item.hint}</div>
                      )}
                    </div>
                    {item.kbd && (
                      <kbd className="px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/[0.08] text-[10px] font-mono text-white/40 shrink-0">{item.kbd}</kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-white/30 font-mono">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><kbd className="px-1 py-0.5 rounded bg-white/[0.06] text-white/50">↑</kbd><kbd className="px-1 py-0.5 rounded bg-white/[0.06] text-white/50">↓</kbd> navigate</span>
            <span className="inline-flex items-center gap-1"><kbd className="px-1 py-0.5 rounded bg-white/[0.06] text-white/50">⏎</kbd> select</span>
          </div>
          <span className="inline-flex items-center gap-1 text-white/40">
            <Zap className="w-3 h-3 text-[#FFD93D]" /> DIS2ORT
          </span>
        </div>
      </div>
    </div>
  );
}
